'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, Search, Calendar, CreditCard, User, Bell, ChevronLeft, ChevronRight, LogOut, Settings, Menu, IndianRupee, Heart, Users, BarChart3, BookOpen, Clock, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  badge?: number;
}

const clientNav: NavItem[] = [
  { label: 'Dashboard', href: '/client-dashboard', icon: <LayoutDashboard size={18} /> },
  { label: 'Find Healer', href: '/find-healers', icon: <Search size={18} /> },
  { label: 'My Bookings', href: '/client-dashboard', icon: <Calendar size={18} /> },
  { label: 'Payments', href: '/client-dashboard', icon: <CreditCard size={18} /> },
  { label: 'Profile', href: '/client-dashboard', icon: <User size={18} /> },
];

const healerNav: NavItem[] = [
  { label: 'Dashboard', href: '/healer-dashboard', icon: <LayoutDashboard size={18} /> },
  { label: 'Sessions', href: '/healer-dashboard', icon: <Calendar size={18} /> },
  { label: 'Availability', href: '/healer-dashboard', icon: <Clock size={18} /> },
  { label: 'Earnings', href: '/healer-dashboard', icon: <IndianRupee size={18} /> },
  { label: 'Profile', href: '/healer-onboarding', icon: <User size={18} /> },
];

const adminNav: NavItem[] = [
  { label: 'Dashboard', href: '/admin-dashboard', icon: <BarChart3 size={18} /> },
  { label: 'Users', href: '/admin-dashboard', icon: <Users size={18} /> },
  { label: 'Healers', href: '/admin-dashboard', icon: <Heart size={18} /> },
  { label: 'Bookings', href: '/admin-dashboard', icon: <BookOpen size={18} /> },
  { label: 'Payments', href: '/admin-dashboard', icon: <IndianRupee size={18} /> },
  { label: 'Settings', href: '/admin-dashboard', icon: <Settings size={18} /> },
];

interface AppLayoutProps {
  children: React.ReactNode;
  userName?: string;
  userAvatar?: string;
  userRole?: 'client' | 'healer' | 'admin';
}

export default function AppLayout({
  children,
  userName,
  userRole,
}: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const pathname = usePathname();
  const { profile, signOut } = useAuth();

  const resolvedRole = userRole || profile?.role || 'client';
  const resolvedName = userName || profile?.full_name || 'User';

  const navItems = resolvedRole === 'healer' ? healerNav : resolvedRole === 'admin' ? adminNav : clientNav;

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
    } catch {
      setSigningOut(false);
    }
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-foreground/30 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:relative z-50 lg:z-auto h-full bg-card border-r border-border flex flex-col
          sidebar-transition shadow-nav
          ${collapsed ? 'w-[68px]' : 'w-64'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className={`h-16 flex items-center border-b border-border px-4 ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {!collapsed && (
            <Link href="/" className="flex items-center gap-2">
              <AppLogo size={32} />
              <div className="flex flex-col leading-none">
                <span className="font-bold text-sm text-foreground">Healing52</span>
                <span className="text-[9px] font-medium text-muted-foreground">Relationship Healing</span>
              </div>
            </Link>
          )}
          {collapsed && (
            <Link href="/"><AppLogo size={32} /></Link>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="hidden lg:flex p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/5 transition-all"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>

        {/* User Summary */}
        {!collapsed && (
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full gradient-healing flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                {resolvedName.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{resolvedName}</p>
                <p className="text-xs text-muted-foreground capitalize">{resolvedRole}</p>
              </div>
            </div>
          </div>
        )}

        {/* Nav Items */}
        <nav className="flex-1 py-3 px-2 overflow-y-auto scrollbar-thin">
          {!collapsed && (
            <p className="px-3 mb-2 text-[10px] font-600 uppercase tracking-widest text-muted-foreground/60">
              Navigation
            </p>
          )}
          <div className="flex flex-col gap-0.5">
            {navItems.map((item) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={`sidebar-${item.label}`}
                  href={item.href}
                  title={collapsed ? item.label : undefined}
                  className={`
                    relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                    transition-all duration-150 group
                    ${isActive ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-primary hover:bg-primary/5'}
                    ${collapsed ? 'justify-center' : ''}
                  `}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {!collapsed && (
                    <>
                      <span className="flex-1">{item.label}</span>
                      {item.badge && (
                        <span className="bg-primary text-primary-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                  {collapsed && item.badge && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary rounded-full" />
                  )}
                  {collapsed && (
                    <span className="absolute left-full ml-2 px-2.5 py-1.5 bg-foreground text-card text-xs font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      {item.label}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-border">
            {!collapsed && (
              <p className="px-3 mb-2 text-[10px] font-600 uppercase tracking-widest text-muted-foreground/60">
                Support
              </p>
            )}
            <div className="flex flex-col gap-0.5">
              {[
                { label: 'Notifications', icon: <Bell size={18} /> },
              ].map((item) => (
                <button
                  key={`sidebar-support-${item.label}`}
                  title={collapsed ? item.label : undefined}
                  className={`
                    relative flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium
                    text-muted-foreground hover:text-primary hover:bg-primary/5 transition-all duration-150 group w-full text-left
                    ${collapsed ? 'justify-center' : ''}
                  `}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {!collapsed && <span className="flex-1">{item.label}</span>}
                  {collapsed && (
                    <span className="absolute left-full ml-2 px-2.5 py-1.5 bg-foreground text-card text-xs font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                      {item.label}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </nav>

        {/* Bottom: Logout */}
        <div className="p-2 border-t border-border">
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            title={collapsed ? 'Logout' : undefined}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-danger hover:bg-danger-light transition-all duration-150 group w-full ${collapsed ? 'justify-center' : ''} disabled:opacity-60`}
          >
            {signingOut ? <Loader2 size={18} className="animate-spin" /> : <LogOut size={18} />}
            {!collapsed && <span>{signingOut ? 'Signing out...' : 'Logout'}</span>}
            {collapsed && !signingOut && (
              <span className="absolute left-full ml-2 px-2.5 py-1.5 bg-foreground text-card text-xs font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                Logout
              </span>
            )}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="h-16 bg-card border-b border-border flex items-center px-4 lg:px-6 gap-4 flex-shrink-0 shadow-nav">
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden p-2 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/5 transition-all"
            aria-label="Open menu"
          >
            <Menu size={20} />
          </button>

          <div className="flex-1 flex items-center gap-3">
            <div className="relative hidden sm:flex items-center">
              <Search size={16} className="absolute left-3 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search healers, sessions..."
                className="pl-9 pr-4 py-2 text-sm bg-muted border border-border rounded-lg w-64 xl:w-80 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-primary transition-all placeholder:text-muted-foreground"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button className="relative p-2.5 rounded-xl border border-border bg-card text-muted-foreground hover:text-primary hover:border-primary/40 transition-all">
              <Bell size={16} />
            </button>
            <div className="w-9 h-9 rounded-full gradient-healing flex items-center justify-center text-white font-semibold text-sm">
              {resolvedName.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto scrollbar-thin">
          {children}
        </main>
      </div>
    </div>
  );
}